import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatDateDMY } from './format';
import { getActiveTheme, resolveThemeName } from './tokens';

function parseYmd(value) {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const d = new Date(value + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export default function DatePicker({ value, onChange, min = '', label = '', className = '' }) {
  const theme = getActiveTheme();
  const themeName = resolveThemeName();
  const isPremiumLight = themeName === 'auroraExecutiveLight' || themeName === 'redLightPremium';
  const selected = parseYmd(value);
  const minDate = parseYmd(min);
  const fallback = useMemo(() => new Date(), []);
  const effectiveSelected = selected || fallback;
  const [open, setOpen] = useState(false);
  const [base, setBase] = useState(new Date(effectiveSelected.getFullYear(), effectiveSelected.getMonth(), 1));
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    // Keep base month in sync when value changes externally
    const d = parseYmd(value);
    if (d) setBase(new Date(d.getFullYear(), d.getMonth(), 1));
  }, [value]);

  const days = useMemo(() => {
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const last = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    const totalDays = last.getDate();
    const firstDowSun0 = first.getDay(); // 0..6, Sunday=0
    const offset = (firstDowSun0 + 6) % 7; // Monday-first offset
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(new Date(base.getFullYear(), base.getMonth(), d));
    // pad to full weeks (multiples of 7)
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [base]);

  function pick(d, e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (!d) return;
    if (minDate && d < minDate) return;
    const ymd = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    onChange && onChange(ymd);
    setOpen(false);
  }

  function clear(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    onChange && onChange('');
    setOpen(false);
  }

  function prevMonth(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setBase(b => new Date(b.getFullYear(), b.getMonth()-1, 1));
  }
  function nextMonth(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setBase(b => new Date(b.getFullYear(), b.getMonth()+1, 1));
  }

  const monthLabel = base.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  const weekdays = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const selYmd = selected
    ? `${selected.getFullYear()}-${String(selected.getMonth()+1).padStart(2,'0')}-${String(selected.getDate()).padStart(2,'0')}`
    : null;
  const buttonText = selected ? formatDateDMY(value) : 'DD/MM/YYYY';
  const today = useMemo(() => new Date(), []);
  const todayDisabled = !!(minDate && today < minDate);

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label ? <label className="text-xs mr-2" style={{ color: theme.mutedText }}>{label}</label> : null}
      <button
        type="button"
        className="border rounded px-2 py-1 text-sm"
        style={{
          background: theme.surface,
          borderColor: theme.border,
          color: theme.text,
        }}
        onClick={() => setOpen(o=>!o)}
      >
        {buttonText}
      </button>
      {open && (
        <div
          className="absolute z-40 mt-1 w-64 rounded border shadow"
          style={{ background: theme.surfaceElevated || theme.surface2 || theme.surface, borderColor: theme.border, color: theme.text }}
        >
          <div className="flex items-center justify-between px-2 py-1 border-b" style={{ borderColor: theme.border }}>
            <button type="button" className="px-2 py-1 text-sm" style={{ color: theme.text }} onClick={prevMonth}>←</button>
            <div className="text-sm font-medium">{monthLabel}</div>
            <button type="button" className="px-2 py-1 text-sm" style={{ color: theme.text }} onClick={nextMonth}>→</button>
          </div>
          <div className="grid grid-cols-7 gap-1 p-2 text-center text-xs">
            {weekdays.map((w,i)=>(<div key={i} style={{ color: theme.mutedText }}>{w}</div>))}
            {days.map((d,i)=> {
              if (!d) return <div key={i} />;
              const isSel = selYmd
                ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` === selYmd
                : false;
              const isDisabled = !!(minDate && d < minDate);
              const selectedBg = isPremiumLight
                ? (theme.primarySoft || 'rgba(0,0,0,0.06)')
                : (theme.selected || theme.primarySoft || theme.primary);
              const selectedBorder = themeName === 'redLightPremium'
                ? 'rgba(215,38,56,0.35)'
                : 'rgba(80,99,168,0.35)';
              return (
                <button
                  type="button"
                  key={i}
                  disabled={isDisabled}
                  className={`px-2 py-1 rounded ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  style={
                    isSel
                      ? (isPremiumLight
                        ? {
                            background: selectedBg,
                            color: theme.text,
                            border: `1px solid ${selectedBorder}`,
                          }
                        : { background: selectedBg, color: theme.text })
                      : { color: theme.text }
                  }
                  onClick={(e)=>pick(d, e)}
                  onMouseEnter={(e) => {
                    if (isDisabled || isSel) return;
                    e.currentTarget.style.background = theme.surface2;
                  }}
                  onMouseLeave={(e) => {
                    if (isDisabled || isSel) return;
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between px-2 py-1 border-t text-xs" style={{ borderColor: theme.border }}>
            <button type="button" className="px-2 py-1 hover:underline" style={{ color: theme.text }} onClick={clear}>Clear</button>
            <button type="button" className={`px-2 py-1 hover:underline ${todayDisabled ? 'opacity-40 cursor-not-allowed' : ''}`} style={{ color: theme.text }} disabled={todayDisabled} onClick={(e) => pick(new Date(), e)}>Today</button>
          </div>
        </div>
      )}
    </div>
  );
}
